/**
 * /superadmin/inbox — two-way SMS/WhatsApp inbox threaded by phone number.
 * Every inbound message hits POST /api/twilio/webhook/inbound (Twilio calls it).
 */
import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, MessageCircle, Smartphone, Send, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

interface Thread { from_number: string; channel: string; last_message: string; last_at: string; count: number; ticket_id?: string; }
interface Msg { _id: string; from_number: string; to_number: string; channel: string; body: string; outbound?: boolean; created_at: string; ticket_id?: string; }

export default function SuperAdminInbox() {
  const [active, setActive] = React.useState<string | null>(null);
  const [reply, setReply] = React.useState("");
  const qc = useQueryClient();

  const threads = useQuery<{ threads: Thread[] }>({
    queryKey: ["inbox-threads"],
    queryFn: async () => (await api.get("/superadmin/inbox?limit=100")).data,
    refetchInterval: 15_000,
  });

  const detail = useQuery<{ from_number: string; messages: Msg[] }>({
    queryKey: ["inbox-thread", active],
    queryFn: async () => (await api.get(`/superadmin/inbox/${encodeURIComponent(active!)}`)).data,
    enabled: !!active,
    refetchInterval: active ? 10_000 : false,
  });

  const send = useMutation({
    mutationFn: async () => (await api.post(`/superadmin/inbox/${encodeURIComponent(active!)}/reply`, {
      channel: detail.data?.messages?.[detail.data.messages.length - 1]?.channel || "sms",
      body: reply,
    })).data,
    onSuccess: (d: any) => {
      if (d?.ok) {
        toast.success("Reply sent"); setReply("");
        qc.invalidateQueries({ queryKey: ["inbox-thread", active] });
        qc.invalidateQueries({ queryKey: ["inbox-threads"] });
      } else {
        toast.error(d?.error || "Reply failed");
      }
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Reply failed"),
  });

  return (
    <DashboardLayout title="Two-way Inbox" subtitle="SMS + WhatsApp replies threaded by phone number. Every inbound message auto-opens or joins a support ticket." seoNoindex>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[calc(100vh-180px)]">
        {/* Thread list */}
        <div className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] overflow-y-auto" data-testid="inbox-threads">
          <div className="sticky top-0 bg-[color:var(--sg-card)] p-3 border-b border-[color:var(--sg-border)]">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-[color:var(--sg-muted)]">Threads ({threads.data?.threads.length || 0})</div>
          </div>
          {threads.isLoading ? (
            <div className="p-6 text-center text-[color:var(--sg-muted)] text-sm"><Loader2 className="h-4 w-4 animate-spin inline mr-1.5"/>Loading…</div>
          ) : (threads.data?.threads || []).length === 0 ? (
            <div className="p-6 text-center text-[color:var(--sg-muted)] text-sm">No inbound messages yet.<br/><span className="text-[11px]">Configure Twilio webhook to <code className="text-[10px] bg-[color:var(--sg-muted-bg)] px-1">/api/twilio/webhook/inbound</code>.</span></div>
          ) : (
            <div className="divide-y divide-[color:var(--sg-border)]">
              {(threads.data?.threads || []).map((t) => (
                <button
                  key={t.from_number}
                  data-testid={`thread-${t.from_number}`}
                  onClick={() => setActive(t.from_number)}
                  className={`w-full text-left p-3 hover:bg-[color:var(--sg-muted-bg)] ${active === t.from_number ? "bg-[color:var(--sg-muted-bg)]" : ""}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 text-xs font-mono truncate text-[color:var(--sg-fg)]">
                      {t.channel === "whatsapp" ? <MessageCircle className="h-3.5 w-3.5 text-emerald-600 shrink-0"/> : <Smartphone className="h-3.5 w-3.5 text-blue-600 shrink-0"/>}
                      {t.from_number}
                    </div>
                    <span className="text-[10px] text-[color:var(--sg-muted)]">{new Date(t.last_at).toLocaleDateString()}</span>
                  </div>
                  <div className="mt-0.5 text-xs text-[color:var(--sg-muted)] line-clamp-1">{t.last_message}</div>
                  {t.ticket_id && <div className="mt-0.5 text-[10px] text-[color:var(--sb-saffron,#F97316)]">Ticket: {t.ticket_id}</div>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Detail */}
        <div className="md:col-span-2 rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] flex flex-col" data-testid="inbox-detail">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-[color:var(--sg-muted)] text-sm">Select a thread to see messages.</div>
          ) : (
            <>
              <div className="p-3 border-b border-[color:var(--sg-border)] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <button onClick={() => setActive(null)} className="md:hidden"><ArrowLeft className="h-4 w-4"/></button>
                  <div>
                    <div className="text-sm font-bold text-[color:var(--sg-fg)] font-mono">{active}</div>
                    <div className="text-[10px] text-[color:var(--sg-muted)]">{detail.data?.messages.length || 0} messages</div>
                  </div>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="inbox-messages">
                {(detail.data?.messages || []).map((m) => (
                  <div key={m._id} className={`flex ${m.outbound ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-lg px-3 py-2 ${m.outbound
                      ? "bg-[color:var(--sb-saffron,#F97316)] text-white"
                      : "bg-[color:var(--sg-muted-bg)] text-[color:var(--sg-fg)]"}`}>
                      <div className="text-sm whitespace-pre-wrap">{m.body}</div>
                      <div className={`mt-1 text-[10px] ${m.outbound ? "text-white/70" : "text-[color:var(--sg-muted)]"}`}>{new Date(m.created_at).toLocaleString()}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-3 border-t border-[color:var(--sg-border)] flex gap-2">
                <input
                  data-testid="inbox-reply"
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && reply.trim() && !send.isPending) send.mutate(); }}
                  placeholder="Type a reply…"
                  className="flex-1 h-10 rounded-md border border-[color:var(--sg-border)] bg-transparent px-3 text-sm"
                />
                <Button
                  data-testid="inbox-send"
                  onClick={() => send.mutate()}
                  disabled={!reply.trim() || send.isPending}
                  className="bg-[color:var(--sb-saffron,#F97316)] hover:bg-[color:var(--sb-saffron-hover,#EA580C)] text-white"
                >
                  {send.isPending ? <Loader2 className="h-4 w-4 animate-spin"/> : <Send className="h-4 w-4"/>}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
